import { useCallback, useEffect, useState } from 'react';

export interface ColumnConfig<T = unknown> {
  id: string;
  header: string;
  accessor: string;
  isVisible: boolean;
  order: number;
  width?: string;
  isCustomRender?: boolean;
  render?: (data: T) => React.ReactNode;
  isStatic?: boolean;
}

interface UseColumnManagementProps<T = unknown> {
  defaultColumns: ColumnConfig<T>[];
  storageKey: string;
}

export const useColumnManagement = <T = unknown>({
  defaultColumns,
  storageKey,
}: UseColumnManagementProps<T>) => {
  const [columns, setColumns] = useState<ColumnConfig<T>[]>(() => {
    // Try to load from localStorage first
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsedColumns = JSON.parse(saved);
        // Merge with default columns to ensure all columns exist
        const mergedColumns = defaultColumns.map((defaultCol) => {
          const savedCol = parsedColumns.find((c: ColumnConfig) => c.id === defaultCol.id);
          return savedCol ? { ...defaultCol, ...savedCol } : defaultCol;
        });
        return mergedColumns.sort((a, b) => a.order - b.order);
      }
    } catch (error) {
      console.warn('Failed to load column preferences:', error);
    }
    return defaultColumns;
  });

  // Save to localStorage whenever columns change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(columns));
    } catch (error) {
      console.warn('Failed to save column preferences:', error);
    }
  }, [columns, storageKey]);

  const toggleColumnVisibility = useCallback((columnId: string) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.id === columnId ? { ...col, isVisible: !col.isVisible } : col
      )
    );
  }, []);

  const reorderColumns = useCallback((fromIndex: number, toIndex: number) => {
    setColumns((prev) => {
      const newColumns = [...prev];
      const [movedColumn] = newColumns.splice(fromIndex, 1);
      newColumns.splice(toIndex, 0, movedColumn);

      // Update order values
      return newColumns.map((col, index) => ({
        ...col,
        order: index,
      }));
    });
  }, []);

  const resetColumns = useCallback(() => {
    setColumns(defaultColumns);
  }, [defaultColumns]);

  const getVisibleColumns = useCallback(() => {
    return columns
      .filter((col) => col.isVisible)
      .sort((a, b) => a.order - b.order);
  }, [columns]);

  const getHiddenColumns = useCallback(() => {
    return columns
      .filter((col) => !col.isVisible)
      .sort((a, b) => a.order - b.order);
  }, [columns]);

  return {
    columns,
    visibleColumns: getVisibleColumns(),
    hiddenColumns: getHiddenColumns(),
    toggleColumnVisibility,
    reorderColumns,
    resetColumns,
  };
};
